import React, { createContext, useContext, useState, useCallback } from 'react';
import { Alert } from 'antd';

type ToastType = 'success' | 'error' | 'warning' | 'info';

interface ToastState {
  type: ToastType;
  message: string;
}

interface ToastContextValue {
  success: (msg: string) => void;
  error: (msg: string) => void;
  warning: (msg: string) => void;
  info: (msg: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const TOAST_DURATION = 4000;

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [toast, setToast] = useState<ToastState | null>(null);

  const show = useCallback((type: ToastType, message: string) => {
    setToast({ type, message });
    const t = setTimeout(() => setToast(null), TOAST_DURATION);
    return () => clearTimeout(t);
  }, []);

  const success = useCallback((msg: string) => show('success', msg), [show]);
  const error = useCallback((msg: string) => show('error', msg), [show]);
  const warning = useCallback((msg: string) => show('warning', msg), [show]);
  const info = useCallback((msg: string) => show('info', msg), [show]);

  return (
    <ToastContext.Provider value={{ success, error, warning, info }}>
      {toast && (
        <div
          style={{
            position: 'fixed',
            top: 24,
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 10001,
            width: '100%',
            maxWidth: 520,
            padding: '0 16px',
          }}
        >
          <Alert
            type={toast.type}
            message={toast.message}
            showIcon
            closable
            onClose={() => setToast(null)}
            style={{ borderRadius: 8 }}
          />
        </div>
      )}
      {children}
    </ToastContext.Provider>
  );
};

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
